// ---------------------------------------------------------------------------
// Live game state. Not persisted: the server is authoritative and a reconnect
// resyncs everything, so a stale cached copy would only ever be wrong.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { CHAT_HISTORY_LIMIT, LOG_HISTORY_LIMIT } from '@countdown/shared';
import type {
  ChatMessage,
  ClientGameState,
  ConnectionQuality,
  GameCostSummary,
  GameLogEntry,
  ServerErrorCode,
} from '@countdown/shared';
import { dropQueuedToasts } from '../lib/toast.js';
import type { ToastNotice, ToastTone } from '../lib/toast.js';
import {
  addOffer,
  restoreAnswer,
  settleOffer,
  type WordOffer,
  type WordRuling,
} from '../lib/wordOffers.js';

/**
 * Why the player is no longer in the room. Without this, being kicked or banned
 * looks exactly like a connection that never came up, and a player who was
 * removed for a reason deserves to be told which.
 *
 * `displaced` is the one that is not a removal: the same player opened the room
 * in a second tab or on a second device and the server handed the seat to the
 * newcomer. It is a dead end for *this* socket only, which is why the room's
 * reconnect token survives it — coming back here takes the seat straight back.
 */
export interface DeadEnd {
  kind: 'kicked' | 'banned' | 'room-closed' | 'displaced';
  reason: string | null;
}

interface GameState {
  state: ClientGameState | null;
  deadEnd: DeadEnd | null;
  /**
   * An error that arrived before we were ever in the room — an unknown code, a
   * full room, a rejected name. Kept apart from toasts because it gates the join
   * form rather than floating past it.
   */
  joinError: { message: string; code?: ServerErrorCode } | null;
  /** Offset between the server's clock and ours, so timers agree. */
  serverTimeOffsetMs: number;
  quality: ConnectionQuality;
  /**
   * When the oldest still-unacknowledged draft was sent, and its sequence.
   *
   * `quality.draftSyncedAt` records the last acknowledgement, which only ever
   * answers "when did the server last hear from us". On its own it cannot tell
   * a draft in flight from one that landed a while ago and has needed no update
   * since, so a player who typed a word and then sat thinking read as "saving
   * your answer" for the rest of the round. Tracking the outstanding sequence
   * answers the question the indicator actually asks.
   */
  draftPendingSince: number | null;
  draftPendingSeq: number | null;
  /**
   * Every word offered in the current letters round, while open answering is
   * on. It lives here rather than on the screen because its rulings arrive on
   * the socket, and it lives on the client at all because the server keeps no
   * attempt history by design — see `lib/wordOffers.ts`.
   */
  wordOffers: WordOffer[];
  /**
   * The queue of lines the socket layer wants a player to see. A bare string is
   * still a valid entry and renders neutral, because most callers have nothing
   * useful to say about tone and should not have to invent one.
   */
  toasts: Array<string | ToastNotice>;
  /**
   * A scheduled restart the server has warned about.
   *
   * It is shown on the toast surface but it is not one of the lines above, and
   * the difference is the whole point: the card it raises never expires, because
   * a message that floats past is no use for something a player needs to still
   * know about thirty seconds later, mid-round. It survives `clear()` for the
   * same reason — the restart is a fact about the server, not about this room,
   * and the announcement usually lands seconds before the socket goes down.
   *
   * `deployAt` is null while a deploy is known to be coming but its landing
   * time is not — see the `maintenance` message in shared.
   *
   * `updateComplete` is set when `/api/config` reports a DIFFERENT buildId from
   * the one this page loaded against, which is the only evidence that the new
   * process is the one answering. It used to be set by a socket reconnect that
   * landed after the announced moment, and that is not the same claim: a phone
   * changing cells reconnects too, and every deploy announcement outlived the
   * deploy by two minutes, so an ordinary blip put "Updated successfully!" and
   * a three-second forced reload in front of somebody mid-round.
   */
  maintenance: { message: string; deployAt: number | null; updateComplete?: boolean } | null;
  /**
   * What the AI opponents in the last finished game cost, and which game that
   * was.
   *
   * It is here rather than on `state` because it does not come with the state:
   * the figure is read out of the spend ledger, which the pure engine cannot
   * touch, so it arrives as its own frame at game-over and again on every
   * arrival while the room is still on the end screen. See the `game-cost`
   * message in shared.
   *
   * **`gameId` is carried so the reader can check it is still the right game.**
   * It is the room's LIVE id, which matches `ClientGameState.liveGameId` and not
   * `gameId`. Nothing clears this on the way back to the lobby, deliberately —
   * a clear would be a second place that has to know when a game stops being
   * the current one, and the room already answers that. The end screen compares
   * the two instead, so a figure from the previous game can never be drawn
   * under the standing of this one.
   */
  gameCost: { gameId: string; cost: GameCostSummary } | null;

  setState: (state: ClientGameState, serverTime: number) => void;
  setQuality: (patch: Partial<ConnectionQuality>) => void;
  /** A draft has gone out and is now awaiting its acknowledgement. */
  markDraftSent: (sequence: number) => void;
  /**
   * The server acknowledged `sequence`. Anything older is acknowledged with it,
   * because a draft is a whole replacement rather than a delta, so a newer one
   * landing makes its predecessors moot.
   */
  markDraftAcked: (sequence: number, at: number) => void;
  appendChat: (message: ChatMessage) => void;
  appendLog: (entry: GameLogEntry) => void;
  /** A word is on its way out. A word already offered is not recorded twice. */
  offerWord: (word: string, actionId: string) => void;
  /** The server's ruling on one offered word. */
  settleWord: (ruling: WordRuling) => void;
  /** Put the answer the server is holding back into an emptied list. */
  restoreOfferedAnswer: (word: string) => void;
  /** Tone is optional: the default is the neutral one every caller had before. */
  pushToast: (message: string, tone?: ToastTone) => void;
  setJoinError: (error: { message: string; code?: ServerErrorCode } | null) => void;
  announceMaintenance: (notice: { message: string; deployAt: number | null }) => void;
  /** What the finished game's AI opponents cost. Replaces whatever was held. */
  setGameCost: (cost: { gameId: string; cost: GameCostSummary }) => void;
  /** Mark the maintenance as complete — the new server is up. */
  completeUpdate: () => void;
  /** Pass a dead end when the room ejected us, so the reason survives the wipe. */
  clear: (deadEnd?: DeadEnd) => void;
}

export const useGameStore = create<GameState>((set) => ({
  state: null,
  deadEnd: null,
  joinError: null,
  serverTimeOffsetMs: 0,
  quality: { rttMs: null, draftSyncedAt: null, status: 'connected' },
  draftPendingSince: null,
  draftPendingSeq: null,
  wordOffers: [],
  toasts: [],
  maintenance: null,
  gameCost: null,

  setState: (state, serverTime) =>
    set((current) => ({
      state,
      // Arriving in a room clears whatever ejected us from the last one, and
      // whatever the join form was complaining about.
      deadEnd: null,
      joinError: null,
      // Timers render against the server's clock, not the device's, which may be
      // minutes out.
      serverTimeOffsetMs: serverTime - Date.now(),
      // A fresh round wipes the record of the last round's draft: there is no
      // answer in this round yet, so the sync indicator has nothing to report
      // until something is typed. The offered words go with it, because they
      // are about a board that is finished with.
      //
      // A reconnect is NOT a round change, so a wifi blip keeps the list. Only
      // a reload loses it, which is the one case the server cannot help with.
      ...(state.currentRoundIndex === current.state?.currentRoundIndex
        ? {}
        : {
            wordOffers: [],
            draftPendingSince: null,
            draftPendingSeq: null,
            quality: { ...current.quality, draftSyncedAt: null },
          }),
    })),

  setQuality: (patch) => set((current) => ({ quality: { ...current.quality, ...patch } })),

  markDraftSent: (sequence) =>
    set((current) => ({
      // Only the first unacknowledged send starts the clock. A fast typist
      // sending four drafts in a row is one wait, not four.
      draftPendingSince: current.draftPendingSince ?? serverNow(),
      draftPendingSeq: sequence,
    })),

  markDraftAcked: (sequence, at) =>
    set((current) => {
      const settled = current.draftPendingSeq === null || sequence >= current.draftPendingSeq;
      return {
        quality: { ...current.quality, draftSyncedAt: at },
        draftPendingSince: settled ? null : current.draftPendingSince,
        draftPendingSeq: settled ? null : current.draftPendingSeq,
      };
    }),

  // Both lists are capped at the SERVER's own limits, imported rather than
  // restated: the engine caps its copy at exactly these two, and a `room-state`
  // broadcast replaces the arrays wholesale, so an uncapped client is symmetry
  // missing rather than a list anybody has watched grow. It is still the
  // difference between a lossless resync and a client that has quietly kept
  // more history than the room it is in.
  appendChat: (message) =>
    set((current) => {
      if (!current.state) return {};
      const chat = [...current.state.chat, message];
      return { state: { ...current.state, chat: chat.slice(-CHAT_HISTORY_LIMIT) } };
    }),

  appendLog: (entry) =>
    set((current) => {
      if (!current.state) return {};
      const log = [...current.state.log, entry];
      return { state: { ...current.state, log: log.slice(-LOG_HISTORY_LIMIT) } };
    }),

  offerWord: (word, actionId) =>
    set((current) => ({ wordOffers: addOffer(current.wordOffers, word, actionId) })),

  settleWord: (ruling) =>
    set((current) => ({ wordOffers: settleOffer(current.wordOffers, ruling) })),

  restoreOfferedAnswer: (word) =>
    set((current) => ({ wordOffers: restoreAnswer(current.wordOffers, word) })),

  // A bare string is kept as a bare string rather than widened here, so a caller
  // that says nothing about tone stays visibly a caller that said nothing.
  pushToast: (message, tone) =>
    set((current) => ({ toasts: [...current.toasts, tone === undefined ? message : { message, tone }] })),

  setJoinError: (joinError) => set({ joinError }),

  announceMaintenance: (notice) => set({ maintenance: notice }),

  setGameCost: (gameCost) => set({ gameCost }),

  completeUpdate: () =>
    set((current) => ({
      maintenance: {
        message: current.maintenance?.message ?? 'Countdown updated.',
        deployAt: current.maintenance?.deployAt ?? null,
        updateComplete: true,
      },
    })),

  clear: (deadEnd) => {
    // Emptying `toasts` only wipes what has not been shown yet. The cards
    // already on screen belong to the room being left, and a library queue
    // outlives this call unless it is told, so being kicked would otherwise
    // leave "Susie joined" floating over the reason you were removed.
    dropQueuedToasts();
    set({
      state: null,
      deadEnd: deadEnd ?? null,
      joinError: null,
      wordOffers: [],
      toasts: [],
      quality: { rttMs: null, draftSyncedAt: null, status: 'offline' },
      draftPendingSince: null,
      draftPendingSeq: null,
      // Cleared, unlike `maintenance`: this one is about a game in a room this
      // client is no longer in, so carrying it into the next room would be a
      // figure with nothing to attach itself to.
      gameCost: null,
      // `maintenance` is not reset here on purpose. See its declaration.
    });
  },
}));

/** Server-aligned now, for every clock render. */
export function serverNow(): number {
  return Date.now() + useGameStore.getState().serverTimeOffsetMs;
}
